import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Music } from "lucide-react";

import {
  advanceStep,
  fetchAudioSelection,
  fetchBackgroundSounds,
  fetchScenarioAudio,
  fetchSelectedScenario,
  fetchSoundCategories,
  deleteBackgroundSound,
  getBackgroundSoundPreviewUrl,
  getScenarioAudioUrl,
  remixWithClips,
  uploadBackgroundSound,
  type AudioClipInstance,
  type TrackDefinition,
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { WaveformPanel } from "@/components/WaveformPanel";

import { TrackContent, type Clip } from "@/components/audio-editor/TrackContent";
import { GalleryModal } from "@/components/audio-editor/GalleryModal";
import { EditorHeader } from "@/components/audio-editor/EditorHeader";
import { TrackHeader } from "@/components/audio-editor/TrackHeader";
import { TimelineScrollArea } from "@/components/audio-editor/TimelineScrollArea";
import { ValidateFooter } from "@/components/audio-editor/ValidateFooter";

import {
  DEFAULT_AMBIENT_TRACK_ID,
  DEFAULT_SFX_TRACK_ID,
  MAX_CLIPS_PER_KIND,
  PX_PER_SEC,
  SFX_CLIP_DUR,
  extractPayload,
  fmtTime,
  type GalleryTab,
} from "@/components/audio-editor/editorConstants";

import { useClipHistory, type CommitClipsFn, type AudioSelectionData } from "@/hooks/useClipHistory";
import { useClipMutations } from "@/hooks/useClipMutations";
import { useClipDrag } from "@/hooks/useClipDrag";
import { useTimelineScrubbing } from "@/hooks/useTimelineScrubbing";

// ─── Main view ────────────────────────────────────────────────────────────────

export function AudioEditorView() {
  const { sessionId, projectName, lastProjectName, updateProgress, setCurrentStep } = useSessionStore();
  const resolvedProjectName = projectName ?? lastProjectName;
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  // ── UI state ─────────────────────────────────────────────────────────────────

  const [isPlaying, setIsPlaying]         = useState(false);
  const [currentTime, setCurrentTime]     = useState(0);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [audioKey, setAudioKey]           = useState(0);
  const [isGenerating, setIsGenerating]   = useState(false);
  const [statusMsg, setStatusMsg]         = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen]    = useState(false);
  const [importedPaths, setImportedPaths]    = useState<Set<string>>(new Set());
  const [playingPreview, setPlayingPreview]  = useState<string | null>(null);
  const [activeTab, setActiveTab]            = useState<GalleryTab>("Sons ambiants");
  const [searchQuery, setSearchQuery]        = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [toolMode, setToolMode]              = useState<"select" | "cut">("select");
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [pxPerSec, setPxPerSec]             = useState(PX_PER_SEC);
  const zoomIn  = () => setPxPerSec((p) => Math.min(80, Math.round(p * 1.4)));
  const zoomOut = () => setPxPerSec((p) => Math.max(6, Math.round(p / 1.4)));

  // ── Volume state ─────────────────────────────────────────────────────────────

  const [masterVolume, setMasterVolume]       = useState(0.85);
  const [narrationVolume, setNarrationVolume] = useState(1.0);
  const [ambientVolume, setAmbientVolume]     = useState(0.5);
  const [sfxVolume, setSfxVolume]             = useState(0.75);
  const [preMuteVolumes, setPreMuteVolumes]   = useState<Record<string, number>>({});
  const [preMuteMaster, setPreMuteMaster]     = useState(0.85);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = masterVolume; }, [masterVolume]);

  // ── Clips & tracks state ──────────────────────────────────────────────────────

  const [clips, setClips]   = useState<AudioClipInstance[]>([]);
  const [tracks, setTracks] = useState<TrackDefinition[]>([]);

  const defaultTracks = useMemo<TrackDefinition[]>(
    () => [
      { id: DEFAULT_AMBIENT_TRACK_ID, kind: "ambient", label: "Ambient 1", volume: 1.0 },
      { id: DEFAULT_SFX_TRACK_ID,     kind: "sfx",     label: "Effets 1",  volume: 1.0 },
    ],
    [],
  );

  // ── Stable refs ───────────────────────────────────────────────────────────────

  const clipsRef          = useRef<AudioClipInstance[]>([]);
  const tracksRef         = useRef<TrackDefinition[]>(defaultTracks);
  const clipsLoadedRef    = useRef(false);
  const narrationVolumeRef = useRef(narrationVolume);
  const ambientVolumeRef   = useRef(ambientVolume);
  const sfxVolumeRef       = useRef(sfxVolume);
  const currentTimeRef     = useRef(0);
  const isPlayingRef       = useRef(false);
  const selectedClipIdRef  = useRef<string | null>(null);
  const audioDurationRef   = useRef<number | null>(null);
  const preservedPlaybackRef = useRef<{ time: number; playing: boolean } | null>(null);
  const audioRef           = useRef<HTMLAudioElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const previewAudioRef    = useRef<HTMLAudioElement | null>(null);
  const lastAppliedGenerationRef = useRef<string | null>(null);

  useEffect(() => { narrationVolumeRef.current = narrationVolume; }, [narrationVolume]);
  useEffect(() => { ambientVolumeRef.current   = ambientVolume;   }, [ambientVolume]);
  useEffect(() => { sfxVolumeRef.current       = sfxVolume;       }, [sfxVolume]);
  useEffect(() => { isPlayingRef.current       = isPlaying;       }, [isPlaying]);
  useEffect(() => { selectedClipIdRef.current  = selectedClipId;  }, [selectedClipId]);
  useEffect(() => { audioDurationRef.current   = audioDuration;   }, [audioDuration]);
  useEffect(() => { clipsRef.current           = clips;           }, [clips]);
  useEffect(() => { tracksRef.current          = tracks;          }, [tracks]);

  // ── Pending file upload state ─────────────────────────────────────────────────

  const [pendingFile, setPendingFile]               = useState<File | null>(null);
  const [pendingTitle, setPendingTitle]             = useState("");
  const [pendingKind, setPendingKind]               = useState<"ambient" | "sfx">("ambient");
  const [isUploadingPending, setIsUploadingPending] = useState(false);
  const [pendingDuration, setPendingDuration]       = useState<number | null>(null);
  const [pendingPlaying, setPendingPlaying]         = useState(false);
  const [localKinds, setLocalKinds]                 = useState<Record<string, "ambient" | "sfx">>({});
  const [localDurations, setLocalDurations]         = useState<Record<string, number>>({});
  const [deletingSoundPath, setDeletingSoundPath]   = useState<string | null>(null);
  const pendingAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!isGalleryOpen) {
      setPendingFile(null); setPendingTitle(""); setPendingKind("ambient");
      setPendingDuration(null);
      if (pendingAudioRef.current) { pendingAudioRef.current.pause(); pendingAudioRef.current = null; }
      setPendingPlaying(false);
      if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
      setPlayingPreview(null);
    }
  }, [isGalleryOpen]);

  useEffect(() => {
    if (!pendingFile) {
      setPendingDuration(null);
      if (pendingAudioRef.current) { pendingAudioRef.current.pause(); pendingAudioRef.current = null; }
      setPendingPlaying(false);
      return;
    }
    const audio = new Audio();
    const url = URL.createObjectURL(pendingFile);
    audio.src = url;
    audio.onloadedmetadata = () => { setPendingDuration(audio.duration); URL.revokeObjectURL(url); };
  }, [pendingFile]);

  const togglePendingPreview = () => {
    if (!pendingFile) return;
    if (pendingPlaying) {
      pendingAudioRef.current?.pause();
      pendingAudioRef.current = null;
      setPendingPlaying(false);
    } else {
      if (playingPreview) { setPlayingPreview(null); previewAudioRef.current?.pause(); previewAudioRef.current = null; }
      const url = URL.createObjectURL(pendingFile);
      const audio = new Audio(url);
      audio.play().catch(() => {});
      audio.onended = () => { setPendingPlaying(false); pendingAudioRef.current = null; };
      pendingAudioRef.current = audio;
      setPendingPlaying(true);
    }
  };

  const handlePendingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) { setPendingFile(dropped); setPendingTitle(dropped.name.replace(/\.[^.]+$/, "")); }
  };

  // ── Audio status indicator ────────────────────────────────────────────────────

  const [isUpdatingAudio, setIsUpdatingAudio] = useState(false);
  const updateAudioTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerUpdateStatus = () => {
    setIsUpdatingAudio(true);
    if (updateAudioTimeoutRef.current) clearTimeout(updateAudioTimeoutRef.current);
    updateAudioTimeoutRef.current = setTimeout(() => setIsUpdatingAudio(false), 3000);
  };

  useEffect(() => () => { if (updateAudioTimeoutRef.current) clearTimeout(updateAudioTimeoutRef.current); }, []);

  // ── Queries ───────────────────────────────────────────────────────────────────

  const selectionQuery = useQuery({
    queryKey: ["selected-scenario", sessionId],
    queryFn:  () => fetchSelectedScenario(sessionId!),
    enabled:  Boolean(sessionId),
  });
  const audioQuery = useQuery({
    queryKey: ["scenario-audio", sessionId],
    queryFn:  () => fetchScenarioAudio(sessionId!),
    enabled:  Boolean(sessionId),
  });
  const audioSelectionQuery = useQuery({
    queryKey: ["audio-selection", sessionId],
    queryFn:  () => fetchAudioSelection(sessionId!),
    enabled:  Boolean(sessionId),
  });
  const soundsQuery = useQuery({
    queryKey: ["background-sounds", searchQuery],
    queryFn:  () => fetchBackgroundSounds(searchQuery || undefined),
  });
  const soundCategoriesQuery = useQuery({
    queryKey: ["sound-categories"],
    queryFn:  fetchSoundCategories,
    staleTime: 60 * 60 * 1000,
  });

  const soundCategories = soundCategoriesQuery.data ?? [];
  const allSounds       = soundsQuery.data ?? [];

  // Sync volumes from server on first load
  useEffect(() => {
    const data = audioSelectionQuery.data;
    if (!data) return;
    if (data.narration_volume != null) setNarrationVolume(data.narration_volume);
    if (data.ambient_volume   != null) setAmbientVolume(data.ambient_volume);
    if (data.sfx_volume       != null) setSfxVolume(data.sfx_volume);
  }, [audioSelectionQuery.data]);

  const audioJobStatus    = audioQuery.data?.status;
  const isAudioProcessing = audioJobStatus === "pending" || audioJobStatus === "running";
  const audioReady        = Boolean(audioQuery.data?.path) && !isAudioProcessing;

  useEffect(() => {
    if (!sessionId || !isAudioProcessing) return;
    const iv = setInterval(() => audioQuery.refetch(), 4000);
    return () => clearInterval(iv);
  }, [sessionId, isAudioProcessing, audioQuery]);

  const audioSrc = useMemo(() => {
    if (!sessionId || !audioReady || !audioQuery.data?.path) return null;
    return `${getScenarioAudioUrl(sessionId)}?k=${audioKey}`;
  }, [audioQuery.data, sessionId, audioReady, audioKey]);

  // ── Playback effects ──────────────────────────────────────────────────────────

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) el.play().catch((err) => console.warn("[AudioEditor] play rejected:", err));
    else el.pause();
  }, [isPlaying]);

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  useEffect(() => {
    if (!audioRef.current) return;
    preservedPlaybackRef.current = { time: currentTimeRef.current, playing: isPlayingRef.current };
    audioRef.current.load();
  }, [audioSrc]);

  useEffect(() => {
    if (!isPlaying || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const playheadPx = currentTime * pxPerSec;
    const { scrollLeft, clientWidth } = container;
    if (playheadPx > scrollLeft + clientWidth - 80 || playheadPx < scrollLeft + 20) {
      container.scrollLeft = Math.max(0, playheadPx - clientWidth / 2);
    }
  }, [currentTime, isPlaying]);

  useEffect(() => () => { previewAudioRef.current?.pause(); }, []);

  // ── Derived data ──────────────────────────────────────────────────────────────

  const durByPathRender = useMemo<Record<string, number>>(() => {
    const lookup: Record<string, number> = {};
    allSounds.forEach((s) => { if (s.duration && s.duration > 0) lookup[s.path] = s.duration; });
    Object.entries(localDurations).forEach(([path, dur]) => { lookup[path] = dur; });
    return lookup;
  }, [allSounds, localDurations]);

  const storyDuration   = audioDuration ?? 60;
  const ambientPathSet  = useMemo(() => new Set(clips.filter((c) => c.kind === "ambient").map((c) => c.source)), [clips]);
  const sfxPathSet      = useMemo(() => new Set(clips.filter((c) => c.kind === "sfx").map((c) => c.source)),     [clips]);

  const timelineDuration = useMemo(() => {
    let maxTime = Math.max(storyDuration, storyDuration);
    clips.forEach((c) => { const end = c.start_seconds + c.duration_seconds; if (end > maxTime) maxTime = end; });
    const anyLaneHasRoom = tracks.some((t) => clips.filter((c) => c.track_id === t.id).length < MAX_CLIPS_PER_KIND);
    if (anyLaneHasRoom) maxTime += SFX_CLIP_DUR + 2;
    return maxTime + 5;
  }, [storyDuration, clips, tracks]);

  const timelineWidth = Math.max(timelineDuration * pxPerSec, 700);

  // ── Ref-forward bridges (break circular hook dependencies) ────────────────────

  const captureSnapshotRef = useRef<() => void>(() => {});
  const commitClipsFnRef   = useRef<CommitClipsFn>(() => {});

  // ── Custom hooks ──────────────────────────────────────────────────────────────

  const history = useClipHistory(
    clipsRef, narrationVolumeRef, ambientVolumeRef, sfxVolumeRef,
    setNarrationVolume, setAmbientVolume, setSfxVolume,
    setSelectedClipId, commitClipsFnRef,
  );

  const mutations = useClipMutations({
    sessionId, resolvedProjectName,
    clipsRef, tracksRef,
    narrationVolumeRef, ambientVolumeRef, sfxVolumeRef,
    storyDuration, durByPathRender,
    audioSelectionData: audioSelectionQuery.data as AudioSelectionData | undefined,
    setClips, setTracks, setAudioKey, setSelectedClipId,
    queryClient, captureSnapshotRef, triggerUpdateStatus,
  });

  // Wire refs immediately (safe: refs are mutable during render)
  captureSnapshotRef.current = history.captureSnapshot;
  commitClipsFnRef.current   = mutations.commitClips;

  // patchClipLive is a simple optimistic update — small enough to stay in the view
  const patchClipLive = (id: string, patch: Partial<AudioClipInstance>) => {
    const next = clipsRef.current.map((c) => (c.id === id ? { ...c, ...patch } : c));
    clipsRef.current = next;
    setClips(next);
  };

  const drag = useClipDrag(
    pxPerSec, clipsRef, patchClipLive, triggerUpdateStatus,
    history.capturePending, history.flushPending, history.discardPending,
    commitClipsFnRef, setSelectedClipId,
  );

  const { handleTimelineMouseDown, handleTimelineWheel } = useTimelineScrubbing({
    scrollContainerRef, audioRef, pxPerSec, storyDuration,
    isPlaying, setIsPlaying, setCurrentTime, currentTimeRef,
    audioDurationRef, selectedClipIdRef,
    isGalleryOpen, showImportModal, audioSrc,
    clipsRef, patchClipLive, captureSnapshotRef, commitClipsFnRef,
    splitClipAt: mutations.splitClipAt,
    removeClip:  mutations.removeClip,
    undo: history.undo, redo: history.redo,
    zoomIn, zoomOut, setToolMode, triggerUpdateStatus,
  });

  // ── Load persisted clips + tracks on first mount ──────────────────────────────

  useEffect(() => {
    const data = audioSelectionQuery.data;
    if (!data || clipsLoadedRef.current) return;
    clipsLoadedRef.current = true;

    const savedTracks = (data.tracks ?? []) as TrackDefinition[];
    const nextTracks: TrackDefinition[] = savedTracks.length > 0 ? savedTracks : defaultTracks;
    const haveDefaultAmbient = nextTracks.some((t) => t.id === DEFAULT_AMBIENT_TRACK_ID);
    const haveDefaultSfx     = nextTracks.some((t) => t.id === DEFAULT_SFX_TRACK_ID);
    const finalTracks: TrackDefinition[] = [
      ...(haveDefaultAmbient ? [] : [defaultTracks[0]]),
      ...(haveDefaultSfx     ? [] : [defaultTracks[1]]),
      ...nextTracks,
    ];
    setTracks(finalTracks);
    tracksRef.current = finalTracks;

    const trackIds = new Set(finalTracks.map((t) => t.id));
    const rawClips = (data.clips ?? []) as AudioClipInstance[];
    const loaded = rawClips.map((c) => {
      const fallback = c.kind === "ambient" ? DEFAULT_AMBIENT_TRACK_ID : DEFAULT_SFX_TRACK_ID;
      const tid = c.track_id && trackIds.has(c.track_id) ? c.track_id : fallback;
      return { ...c, track_id: tid };
    });
    clipsRef.current = loaded;
    setClips(loaded);
  }, [audioSelectionQuery.data, defaultTracks]);

  // Reload when AI agent modifies clips server-side
  useEffect(() => {
    const handler = () => { clipsLoadedRef.current = false; audioSelectionQuery.refetch(); };
    window.addEventListener("agent-clips-updated", handler);
    return () => window.removeEventListener("agent-clips-updated", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply SFX placements from ElevenLabs alignment
  useEffect(() => {
    const meta = audioQuery.data as Record<string, unknown> | undefined;
    const placements = meta?.sfx_placements as Array<{ path: string; timestamp_s: number }> | undefined;
    if (!placements?.length) return;
    const genAt = meta?.generated_at as string | undefined;
    if (!genAt || genAt === lastAppliedGenerationRef.current) return;
    if (!sessionId || !clipsLoadedRef.current) return;
    lastAppliedGenerationRef.current = genAt;

    const existingSources = new Set(clipsRef.current.filter((c) => c.kind === "sfx").map((c) => c.source));
    const newClips: AudioClipInstance[] = placements
      .filter((p) => !existingSources.has(p.path))
      .map((p) => ({
        id: `clip_${Math.random().toString(36).slice(2, 12)}`,
        source: p.path, kind: "sfx" as const, track_id: DEFAULT_SFX_TRACK_ID,
        start_seconds: p.timestamp_s, source_offset_seconds: 0,
        duration_seconds: durByPathRender[p.path] ?? SFX_CLIP_DUR,
        volume: 1, fade_in_ms: 0, fade_out_ms: 0,
      }));
    if (newClips.length === 0) return;
    mutations.commitClips([...clipsRef.current, ...newClips], { remix: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioQuery.data, sessionId, durByPathRender]);

  // ── Gallery sound filter ──────────────────────────────────────────────────────

  const UNCLASSIFIED_LABEL = "Non classé";
  const categoryOptions = useMemo(
    () => [...soundCategories.map((c) => c.label), UNCLASSIFIED_LABEL],
    [soundCategories],
  );
  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) => { const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next; });
  };

  const filteredSounds = useMemo(() => {
    let list = allSounds;
    if (activeTab === "Sons ambiants") {
      list = list.filter((s) => {
        const kind = localKinds[s.path] || s.suggested_kind || (s.duration && s.duration >= 15 ? "ambient" : "sfx");
        return kind === "ambient";
      });
    } else if (activeTab === "Effets sonores") {
      list = list.filter((s) => {
        const kind = localKinds[s.path] || s.suggested_kind || (s.duration && s.duration >= 15 ? "ambient" : "sfx");
        return kind === "sfx";
      });
    } else if (activeTab === "Mes sons") {
      list = list.filter((s) => s.imported || importedPaths.has(s.path));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    if (selectedCategories.size > 0) {
      list = list.filter((s) =>
        s.category ? selectedCategories.has(s.category) : selectedCategories.has(UNCLASSIFIED_LABEL),
      );
    }
    return list;
  }, [allSounds, searchQuery, selectedCategories, activeTab, importedPaths, localKinds]);

  // ── Preview playback ──────────────────────────────────────────────────────────

  const togglePreview = (sound: { path: string }) => {
    if (playingPreview === sound.path) {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      setPlayingPreview(null);
    } else {
      previewAudioRef.current?.pause();
      const audio = new Audio(getBackgroundSoundPreviewUrl(sound.path));
      audio.play().catch(() => {});
      audio.onended = () => setPlayingPreview(null);
      previewAudioRef.current = audio;
      setPlayingPreview(sound.path);
    }
  };

  // ── Volume helpers ────────────────────────────────────────────────────────────

  const handleTrackVolumeChange = (trackKindLabel: string, newVolume: number) => {
    history.captureSnapshot();
    triggerUpdateStatus();
    const vols = {
      narration: trackKindLabel === "Narration"     ? newVolume : narrationVolume,
      ambient:   trackKindLabel === "Ambient"       ? newVolume : ambientVolume,
      sfx:       trackKindLabel === "Sound Effects" ? newVolume : sfxVolume,
    };
    mutations.persistClips(clipsRef.current, vols);
    void mutations.remixClips(clipsRef.current, vols);
  };

  const toggleTrackMute = (trackKindLabel: string, currentVol: number, setVol: (v: number) => void) => {
    if (currentVol > 0) {
      setPreMuteVolumes((prev) => ({ ...prev, [trackKindLabel]: currentVol }));
      setVol(0);
      handleTrackVolumeChange(trackKindLabel, 0);
    } else {
      const restored = preMuteVolumes[trackKindLabel] ?? 1.0;
      setVol(restored);
      handleTrackVolumeChange(trackKindLabel, restored);
    }
  };

  const toggleMasterMute = () => {
    if (masterVolume > 0) { setPreMuteMaster(masterVolume); setMasterVolume(0); }
    else setMasterVolume(preMuteMaster || 0.85);
  };

  // ── Import pending sound ──────────────────────────────────────────────────────

  const handleImportPendingSound = async () => {
    if (!pendingFile) return;
    setIsUploadingPending(true);
    try {
      const title    = pendingTitle.trim() || pendingFile.name.replace(/\.[^.]+$/, "");
      const uploaded = await uploadBackgroundSound(title, pendingFile);
      setLocalKinds((prev)     => ({ ...prev, [uploaded.path]: pendingKind }));
      setLocalDurations((prev) => ({ ...prev, [uploaded.path]: uploaded.duration ?? pendingDuration ?? 0 }));
      setImportedPaths((prev)  => { const next = new Set(prev); next.add(uploaded.path); return next; });
      await queryClient.invalidateQueries({ queryKey: ["background-sounds"] });
      mutations.addClip(uploaded.path, pendingKind);
      setIsGalleryOpen(false);
      setPendingFile(null); setPendingTitle(""); setPendingDuration(null); setPendingPlaying(false);
    } catch (err) {
      console.error("Error importing pending sound:", err);
    } finally {
      setIsUploadingPending(false);
    }
  };

  const handleDeleteSound = async (sound: { path: string; name: string }) => {
    const usedInTimeline = clipsRef.current.some((c) => c.source === sound.path);
    const confirmMessage = usedInTimeline
      ? `Ce son est utilisé dans la timeline actuelle. Le supprimer de la galerie peut casser ce montage. Continuer ?`
      : `Supprimer définitivement "${sound.name}" de la galerie ?`;
    if (!window.confirm(confirmMessage)) return;

    setDeletingSoundPath(sound.path);
    try {
      if (playingPreview === sound.path) {
        previewAudioRef.current?.pause();
        previewAudioRef.current = null;
        setPlayingPreview(null);
      }

      await deleteBackgroundSound(sound.path);
      setImportedPaths((prev) => {
        const next = new Set(prev);
        next.delete(sound.path);
        return next;
      });
      setLocalKinds((prev) => {
        const next = { ...prev };
        delete next[sound.path];
        return next;
      });
      setLocalDurations((prev) => {
        const next = { ...prev };
        delete next[sound.path];
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: ["background-sounds"] });
    } catch (err) {
      console.error("Error deleting sound:", err);
      setStatusMsg("Impossible de supprimer ce son pour le moment.");
    } finally {
      setDeletingSoundPath(null);
    }
  };

  // ── Validate ──────────────────────────────────────────────────────────────────

  const handleValidate = async () => {
    if (!sessionId) return;
    if (!audioReady) { setStatusMsg("L'audio n'est pas encore prêt."); return; }
    setIsGenerating(true);
    setStatusMsg("Finalisation du mix…");
    try {
      await remixWithClips(sessionId, clipsRef.current, { narrationVolume, ambientVolume, sfxVolume });
      await advanceStep(sessionId, "scenario_edit", {});
      updateProgress({ scenarioEdited: true });
      setCurrentStep("final_validation");
      navigate("/step/final_validation");
    } catch {
      setStatusMsg("Erreur lors de la finalisation. Réessayez.");
      setIsGenerating(false);
    }
  };

  if (!sessionId) return null;

  // ── Track rows (for TrackHeader) ──────────────────────────────────────────────

  type TrackRow = {
    id: string; label: string; type: string; volume: number;
    setVolume: (v: number) => void; color: string; kind?: "ambient" | "sfx";
    isNarration: boolean; isDeletable: boolean; volumeKindLabel: string;
  };

  const trackRows: TrackRow[] = [
    {
      id: "narration", label: "Narration", type: "Piste principale",
      volume: narrationVolume, setVolume: setNarrationVolume, color: "#0f172b",
      isNarration: true, isDeletable: false, volumeKindLabel: "Narration",
    },
    ...tracks.map<TrackRow>((t) => {
      const isDefault = t.id === DEFAULT_AMBIENT_TRACK_ID || t.id === DEFAULT_SFX_TRACK_ID;
      const sharedVol = t.kind === "ambient" ? ambientVolume : sfxVolume;
      const setSharedVol = t.kind === "ambient" ? setAmbientVolume : setSfxVolume;
      return {
        id: t.id, label: t.label,
        type: t.kind === "ambient" ? "Ambiance" : "Effet sonore",
        volume: sharedVol, setVolume: setSharedVol,
        color: t.kind === "ambient" ? "#04a404" : "#c8009c",
        kind: t.kind, isNarration: false, isDeletable: !isDefault,
        volumeKindLabel: t.kind === "ambient" ? "Ambient" : "Sound Effects",
      };
    }),
  ];

  const canAddAmbientTrack = tracks.filter((t) => t.kind === "ambient").length < 6;
  const canAddSfxTrack     = tracks.filter((t) => t.kind === "sfx").length     < 6;

  // ── Narration clip (locked, full duration) ────────────────────────────────────

  const narrationClipsWave: Clip[] = [{
    id: "narration", label: "Narration", variant: "Story",
    start: 0, end: storyDuration > 0 ? storyDuration : 60,
    waveformUrl: sessionId ? getScenarioAudioUrl(sessionId) : undefined,
    waveformStartFrac: 0, waveformEndFrac: 1,
  }];

  const narrationBadges = useMemo<Array<{ atSec: number; n: number }>>(() => {
    if (!selectionQuery.data) return [];
    const raw     = selectionQuery.data as Record<string, unknown>;
    const payload = extractPayload(raw);
    const parts   = Array.isArray(payload.parties) ? (payload.parties as Array<Record<string, unknown>>) : [];
    if (parts.length === 0) return [];
    const texts      = parts.map((p) => ((p.texte_narration ?? p.texte) as string) ?? "");
    const totalChars = texts.reduce((s, t) => s + t.length, 0) || 1;
    let cursor = 0;
    return parts.map((_, idx) => {
      const dur = (texts[idx].length / totalChars) * storyDuration;
      const atSec = cursor;
      cursor += dur;
      return { atSec, n: idx + 1 };
    });
  }, [selectionQuery.data, storyDuration]);

  // ── Per-track clip builder ────────────────────────────────────────────────────

  const buildClipsForTrack = (track: TrackDefinition): Clip[] => {
    const fallback = track.kind === "ambient" ? DEFAULT_AMBIENT_TRACK_ID : DEFAULT_SFX_TRACK_ID;
    const lane     = clips.filter((c) => (c.track_id ?? fallback) === track.id);
    const blocks: Clip[] = lane.map((clip, idx) => {
      const srcDur = durByPathRender[clip.source];
      const name   =
        allSounds.find((s) => s.path === clip.source)?.name ??
        clip.source.split("/").pop()?.replace(/\.[^.]+$/, "") ??
        (clip.kind === "ambient" ? `Ambiance ${idx + 1}` : `Effet ${idx + 1}`);
      return {
        id: clip.id, label: name,
        variant: clip.kind === "ambient" ? "Ambient" : "Effet sonore",
        start: clip.start_seconds, end: clip.start_seconds + clip.duration_seconds,
        selected: selectedClipId === clip.id,
        onDragStart: (e) => drag.handleClipDragStart(clip.id, e),
        onDurationResizeStart: (e) => drag.handleDurationResizeStart(clip.id, e),
        onStartResizeStart:    (e) => drag.handleStartResizeStart(clip.id, e),
        onDelete: () => mutations.removeClip(clip.id),
        waveformUrl:       getBackgroundSoundPreviewUrl(clip.source),
        waveformStartFrac: srcDur ? clip.source_offset_seconds / srcDur : 0,
        waveformEndFrac:   srcDur ? (clip.source_offset_seconds + clip.duration_seconds) / srcDur : 1,
        cutMode: toolMode === "cut",
        onCut: (clickXInClip: number) =>
          mutations.splitClipAt(clip.id, clip.start_seconds + clickXInClip / pxPerSec),
      };
    });
    if (lane.length < MAX_CLIPS_PER_KIND) {
      const lastEnd  = lane.reduce((m, c) => Math.max(m, c.start_seconds + c.duration_seconds), 0);
      const addStart = lastEnd > 0 ? lastEnd + 2 : 0;
      blocks.push({
        id: `add-${track.id}`,
        label: track.kind === "ambient" ? "Ajouter une ambiance" : "Ajouter un effet sonore",
        variant: track.kind === "ambient" ? "AjouterAmbient" : "Ajouter",
        start: addStart, end: addStart + SFX_CLIP_DUR,
        onClick: () => {
          mutations.pendingAddTrackIdRef.current = track.id;
          setIsGalleryOpen(true);
          setActiveTab(track.kind === "ambient" ? "Sons ambiants" : "Effets sonores");
        },
      });
    }
    return blocks;
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto flex w-full max-w-[1100px] min-w-0 flex-col gap-4">

      {/* ── Block 1 – Timeline ─────────────────────────────────────────────── */}
      <div className="rounded-[14px] border border-[#8ea4bd] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)] overflow-hidden">

        {/* Title header */}
        <div className="flex items-center gap-2 border-b border-[#e2e8f0] bg-[#f8fafc] px-5 py-4">
          <Music className="h-4 w-4 text-[#45556c] shrink-0" />
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[14px] font-medium text-[#45556c] leading-none">Édition de l'audio</h2>
            <p className="text-[13px] text-[#94a3b8] leading-none">
              Personnaliser l'audio pour le transformer en artefact.
            </p>
          </div>
        </div>


        <div className="flex flex-col min-w-0 overflow-hidden">

          {/* Action bar */}
          <EditorHeader
            isPlaying={isPlaying}
            audioSrc={audioSrc}
            currentTime={currentTime}
            storyDuration={storyDuration}
            isUpdatingAudio={isUpdatingAudio}
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            toolMode={toolMode}
            fmtTime={fmtTime}
            onPlayPause={() => setIsPlaying((v) => !v)}
            onSkipBack={() => { if (audioRef.current) audioRef.current.currentTime = 0; setCurrentTime(0); }}
            onUndo={history.undo}
            onRedo={history.redo}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onToolModeToggle={() => setToolMode((m) => (m === "cut" ? "select" : "cut"))}
            onOpenGallery={() => { mutations.pendingAddTrackIdRef.current = null; setIsGalleryOpen(true); }}
          />

          {/* Waveform overview */}
          {audioSrc && (
            <div className="border-b border-[#e2e8f0] bg-[#f8fafc]">
              <WaveformPanel />
            </div>
          )}

          {/* Timeline: fixed labels + scrollable clip area */}
          <div className="flex border-t border-[#e2e8f0] min-w-0 overflow-hidden">
            <TrackHeader
              masterVolume={masterVolume}
              trackRows={trackRows}
              canAddAmbientTrack={canAddAmbientTrack}
              canAddSfxTrack={canAddSfxTrack}
              onMasterMuteToggle={toggleMasterMute}
              onMasterVolumeChange={setMasterVolume}
              onTrackMuteToggle={toggleTrackMute}
              onTrackVolumeChangeCommit={handleTrackVolumeChange}
              onRemoveTrack={mutations.removeTrack}
              onAddAmbientTrack={() => mutations.addTrack("ambient")}
              onAddSfxTrack={() => mutations.addTrack("sfx")}
            />
            <TimelineScrollArea
              scrollContainerRef={scrollContainerRef}
              clipDrag={drag.clipDrag}
              timelineWidth={timelineWidth}
              timelineDuration={timelineDuration}
              pxPerSec={pxPerSec}
              storyDuration={storyDuration}
              currentTime={currentTime}
              narrationClipsWave={narrationClipsWave}
              narrationBadges={narrationBadges}
              tracks={tracks}
              canAddAmbientTrack={canAddAmbientTrack}
              canAddSfxTrack={canAddSfxTrack}
              buildClipsForTrack={buildClipsForTrack}
              onTimelineMouseDown={handleTimelineMouseDown}
              onWheel={handleTimelineWheel}
            />
          </div>
        </div>

        <ValidateFooter
          isGenerating={isGenerating}
          isAudioProcessing={isAudioProcessing}
          isRemixing={mutations.isRemixing}
          onValidate={handleValidate}
        />

        {/* Hidden audio element */}
        {audioSrc && (
          <audio
            key="scenario-audio"
            ref={audioRef}
            src={audioSrc}
            className="hidden"
            onLoadedMetadata={(e) => {
              const el = e.target as HTMLAudioElement;
              setAudioDuration(el.duration || null);
              const preserved = preservedPlaybackRef.current;
              preservedPlaybackRef.current = null;
              if (preserved && preserved.time > 0 && el.duration && preserved.time < el.duration)
                el.currentTime = preserved.time;
              if (preserved && preserved.playing) el.play().catch(() => {});
            }}
            onTimeUpdate={(e) => {
              const t = (e.target as HTMLAudioElement).currentTime;
              currentTimeRef.current = t;
              setCurrentTime(t);
            }}
            onEnded={() => setIsPlaying(false)}
          />
        )}
      </div>

      {/* ── Gallery modal ─────────────────────────────────────────────────── */}
      <GalleryModal
        open={isGalleryOpen}
        onClose={() => { mutations.pendingAddTrackIdRef.current = null; setIsGalleryOpen(false); }}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        soundsQuery={soundsQuery}
        filteredSounds={filteredSounds}
        ambientPathSet={ambientPathSet}
        sfxPathSet={sfxPathSet}
        playingPreview={playingPreview}
        togglePreview={togglePreview}
        addClip={mutations.addClip}
        onDeleteSound={handleDeleteSound}
        deletingSoundPath={deletingSoundPath}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        pendingTitle={pendingTitle}
        setPendingTitle={setPendingTitle}
        pendingKind={pendingKind}
        setPendingKind={setPendingKind}
        isUploadingPending={isUploadingPending}
        pendingDuration={pendingDuration}
        pendingPlaying={pendingPlaying}
        togglePendingPreview={togglePendingPreview}
        handlePendingDrop={handlePendingDrop}
        handleImportPendingSound={handleImportPendingSound}
      />
    </div>
  );
}
